/**
 * useDemoProject.js - Hook per gestire progetti demo automaticamente
 * Se utente NON ha progetti, crea automaticamente un demo dal template
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import {
  checkUserHasProjects,
  createDemoProjectFromTemplate,
  getUserProjects,
  convertDemoToReal,
  duplicateProject
} from '../services/demoProjectService';

/**
 * Hook to automatically manage demo projects
 * - Checks if user has projects
 * - If not, creates demo from template
 * - Provides methods for demo-to-real conversion
 * 
 * @returns {{
 *   projects: Array,
 *   loading: boolean,
 *   error: Error|null,
 *   hasDemoProject: boolean,
 *   createDemo: Function,
 *   convertToReal: Function,
 *   duplicate: Function,
 *   refetch: Function
 * }}
 */
export const useDemoProject = () => {
  const { user, profile, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Check if user has demo project
  const hasDemoProject = projects.some(p => p.is_demo);
  
  // Get demo project (if exists)
  const demoProject = projects.find(p => p.is_demo);

  /**
   * Fetch user's projects
   */
  const fetchProjects = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await getUserProjects(user.id);
      
      if (fetchError) {
        console.error('📋 Fetch projects error:', fetchError);
        setError(fetchError);
      } else {
        setProjects(data);
      }
    } catch (err) {
      console.error('📋 Fetch projects exception:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  /**
   * Initialize demo project if user has none
   */
  const initializeDemoIfNeeded = useCallback(async () => {
    if (!user?.id || !profile?.role_professional || initialized) return;
    
    try {
      // Check if user has any projects
      const { hasProjects, error: checkError } = await checkUserHasProjects(user.id);
      
      if (checkError) {
        console.error('📋 Check projects error:', checkError);
        return;
      }
      
      // If no projects, create demo
      if (!hasProjects) {
        console.log('📋 No projects found, creating demo for role:', profile.role_professional);
        
        const { data: demoProject, error: createError } = await createDemoProjectFromTemplate(
          user.id,
          profile.role_professional
        );
        
        if (createError) {
          console.error('📋 Create demo error:', createError);
        } else if (demoProject) {
          console.log('📋 Demo project created successfully:', demoProject.name);
          // Refetch projects to include the new demo
          await fetchProjects();
        }
      }
      
      setInitialized(true);
    } catch (err) {
      console.error('📋 Initialize demo error:', err);
    }
  }, [user?.id, profile?.role_professional, initialized, fetchProjects]);

  /**
   * Convert demo project to real project
   * @param {string} projectId - Project UUID
   */
  const convertToReal = useCallback(async (projectId) => {
    try {
      const { data, error: convertError } = await convertDemoToReal(projectId);
      
      if (convertError) {
        console.error('📋 Convert to real error:', convertError);
        return { data: null, error: convertError };
      }
      
      // Update local state
      setProjects(prev => prev.map(p => 
        p.id === projectId ? { ...p, is_demo: false } : p
      ));
      
      return { data, error: null };
    } catch (err) {
      console.error('📋 Convert to real exception:', err);
      return { data: null, error: err };
    }
  }, []);

  /**
   * Duplicate a project
   * @param {string} projectId - Project UUID
   * @param {string} newName - Optional new name
   */
  const duplicate = useCallback(async (projectId, newName = null) => {
    if (!user?.id) return { data: null, error: new Error('No user') };
    
    try {
      const { data, error: dupError } = await duplicateProject(projectId, user.id, newName);
      
      if (dupError) {
        console.error('📋 Duplicate error:', dupError);
        return { data: null, error: dupError };
      }
      
      // Refetch projects
      await fetchProjects();
      
      return { data, error: null };
    } catch (err) {
      console.error('📋 Duplicate exception:', err);
      return { data: null, error: err };
    }
  }, [user?.id, fetchProjects]);

  /**
   * Manually create a demo project
   */
  const createDemo = useCallback(async () => {
    if (!user?.id || !profile?.role_professional) {
      return { data: null, error: new Error('No user or profile') };
    }
    
    try {
      const { data, error: createError } = await createDemoProjectFromTemplate(
        user.id,
        profile.role_professional
      );
      
      if (createError) {
        return { data: null, error: createError };
      }
      
      // Refetch projects
      await fetchProjects();
      
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  }, [user?.id, profile?.role_professional, fetchProjects]);

  // Initial fetch
  useEffect(() => {
    if (!authLoading && user?.id) {
      fetchProjects();
    }
  }, [authLoading, user?.id, fetchProjects]);

  // Initialize demo after projects are loaded
  useEffect(() => {
    if (!authLoading && !loading && user?.id && profile?.role_professional) {
      initializeDemoIfNeeded();
    }
  }, [authLoading, loading, user?.id, profile?.role_professional, initializeDemoIfNeeded]);

  return {
    projects,
    loading: loading || authLoading,
    error,
    hasDemoProject,
    demoProject,
    createDemo,
    convertToReal,
    duplicate,
    refetch: fetchProjects
  };
};

export default useDemoProject;
